// --------------------------------------------------------------
// Created On: 2025-05-01
// Author: Hannah Vaughan
//
// Last Modified: 2025-10-27
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment } from "react";
import styles from "./ProductPage.module.scss";
import IconTooltip from "src/components/IconTooltip/IconTooltip";
import { MAX_ASSET_FIELDS_LENGTH, PRODUCT_MANUFACTURERS } from "src/constants/miscellaneous";

// Page of the Asset Management Wizard that allows the user to select and enter product information for an asset.
export default function ProductPage(props: Props): Component {
  return (
    <Fragment>
      <div className="col mx-3">
        <label className={`${styles.title} mb-3`}>Product Type&nbsp;</label>
        <IconTooltip
          id="product-type-tooltip"
          icon="info-circle"
          message={
            "Product type describes the main section of the asset that " +
            "we are wanting to monitor and/or operate remotely."
          }
          color="var(--info-tooltip)"
        />
        <select
          data-test="product-type-selection"
          className="form-select"
          value={props.productType}
          disabled={props.disabled}
          onChange={(e) => props.onChangeProductType(e.target.value)}
        >
          <option value="" disabled>
            Please select a product type...
          </option>
          {props.productTypes.map((productType) => (
            <option key={productType.productTypeId} value={productType.name}>
              {productType.name}
            </option>
          ))}
        </select>
      </div>
      <div className="col m-3">
        <label className={`${styles.title} mb-3`}>Product Manufacturer&nbsp;</label>
        <IconTooltip
          id="product-manufacturer-tooltip"
          icon="info-circle"
          message={
            "The product manufacturer is the company that produces the selected product. For example, " +
            "if the product is a pump, this refers to the pump manufacturer."
          }
          color="var(--info-tooltip)"
        />
        <select
          data-test="product-manufacturer-selection"
          className="form-select"
          value={props.productManufacturer}
          disabled={props.disabled}
          onChange={(e) => props.onChangeProductManufacturer(e.target.value)}
        >
          <option value="" disabled>
            Please select a product manufacturer...
          </option>
          {PRODUCT_MANUFACTURERS.map((productManufacturer) => (
            <option key={productManufacturer.code} value={productManufacturer.code}>
              {productManufacturer.name}
            </option>
          ))}
          <option value="OTHER">Other...</option>
        </select>
      </div>
      {props.productManufacturer === "OTHER" && (
        <div className="col m-3">
          <input
            data-test="product-manufacturer-custom-input"
            className="form-control mx-auto"
            value={props.otherProductManufacturer}
            type="text"
            maxLength={MAX_ASSET_FIELDS_LENGTH}
            disabled={props.disabled}
            onChange={(e) => props.onChangeOtherProductManufacturer(e.target.value)}
          />
        </div>
      )}
      <div className="col m-3">
        <label className={`${styles.title} mb-3`}>Product Part Number&nbsp;</label>
        <IconTooltip
          id="product-part-number-tooltip"
          icon="info-circle"
          message="The product's bill of materials identifier."
          color="var(--info-tooltip)"
        />
        <input
          data-test="product-part-number-input"
          className="form-control mx-auto"
          type="text"
          maxLength={MAX_ASSET_FIELDS_LENGTH}
          value={props.productModel}
          disabled={props.disabled}
          onChange={(e) => props.onChangeProductModel(e.target.value)}
        />
      </div>
      <div className="col m-3">
        <label className={`${styles.title} mb-3`}>Product Serial Number&nbsp;</label>
        <IconTooltip
          id="product-serial-number-tooltip"
          icon="info-circle"
          message="The serial number found on the product."
          color="var(--info-tooltip)"
        />
        <input
          data-test="product-serial-number-input"
          className="form-control mx-auto"
          type="text"
          maxLength={MAX_ASSET_FIELDS_LENGTH}
          value={props.productIdentifier}
          disabled={props.disabled}
          onChange={(e) => props.onChangeProductIdentifier(e.target.value)}
        />
      </div>
    </Fragment>
  );
}

interface Props {
  productType: string;
  productTypes: ProductType[];
  productManufacturer: string;
  otherProductManufacturer: string;
  productModel: string;
  productIdentifier: string;
  disabled: boolean;
  onChangeProductType: (productType: string) => void;
  onChangeProductManufacturer: (productManufacturer: string) => void;
  onChangeOtherProductManufacturer: (otherProductManufacturer: string) => void;
  onChangeProductModel: (productModel: string) => void;
  onChangeProductIdentifier: (productIdentifier: string) => void;
}

interface ProductType {
  productTypeId: number;
  name: string;
}
